import { distance as turfDistance } from '@turf/turf';
import type { MapMatchResult } from './mapMatch';

/**
 * Map matching without detours.
 *
 * A matcher has to reach every point it's sent, so a point that happens to sit nearest a side
 * path pulls the match out along that path and straight back (or round a tiny loop) — the
 * "takes you out of the way and back in" the user sees. Two things keep that out:
 *
 * - fewer points: one every 50 m rather than every vertex of the drawn line, so far fewer of
 *   them land nearest the wrong path;
 * - after each match, find any detour, drop the points that caused it and ask again, so
 *   GraphHopper itself draws the clean line, turns and all, rather than us cutting its answer.
 *   A long spur (a dead end running beside the drawn line, say) can swallow several points, so
 *   every point nearer the detour than the rest of the line goes, not just the one at its tip.
 *
 * A detour the author drew (the original line goes out there and back too) is kept.
 * Same rules and constants as the apps (MapMatchDetours on Android, iOS).
 */

/** One point sent per this many metres along the line. */
export const MATCH_SAMPLE_SPACING_M = 50;
/** Our GraphHopper plan rejects a match request with more points than this. */
export const MAX_MATCH_POINTS = 500;
/** A detour comes back to within this of where it left the line. */
export const RETURN_M = 5;
/** …goes at least this far out… */
export const MIN_REACH_M = 10;
/** …and is at most this long, there and back: spurs of up to a kilometre each way. */
export const MAX_DETOUR_M = 2000;
/**
 * How near the original line has to pass a detour's start to count as going through it: up to
 * 40 m (how far the matcher looks for a path around each point, so a long out-and-back the
 * author traced a little off the path still counts as theirs), but no more than half the
 * detour's reach (at least 5 m), so a small out-and-back near its far end isn't swallowed whole
 * by the radius.
 */
export const NEAR_M = 40;
export const nearFor = (reach: number): number => Math.min(NEAR_M, Math.max(RETURN_M, reach / 2));
/** Samples this much further from a detour's tip than the nearest one are dropped with it. */
export const DROP_SLACK_M = 10;
/** Requests per day at most: the first match and three retries. */
export const MAX_MATCH_ATTEMPTS = 4;

type LngLat = number[];

const metres = (a: LngLat, b: LngLat): number => turfDistance([a[0], a[1]], [b[0], b[1]], { units: 'meters' });

/** Earth radius turf uses, for the flat maths below. */
const EARTH_RADIUS_M = 6371008.8;

/**
 * Metres from `p` to the segment `a`–`b`, on a flat projection around `p` (exact enough over the
 * few hundred metres a matched segment spans). Matched lines only have vertices at bends, so a
 * point beside a long straight is measured to the straight, not to its far-off ends.
 */
export function metresToSegment(p: LngLat, a: LngLat, b: LngLat): number {
  const k = (Math.PI / 180) * EARTH_RADIUS_M;
  const cosLat = Math.cos((p[1] * Math.PI) / 180);
  const ax = (a[0] - p[0]) * cosLat * k;
  const ay = (a[1] - p[1]) * k;
  const bx = (b[0] - p[0]) * cosLat * k;
  const by = (b[1] - p[1]) * k;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSq));
  return Math.hypot(ax + t * dx, ay + t * dy);
}

/** The nearest `p` comes to `line` between vertices `from` and `to` (inclusive), in metres. */
function metresToStretch(p: LngLat, line: LngLat[], from: number, to: number): number {
  let best = Infinity;
  for (let k = from; k < to; k++) best = Math.min(best, metresToSegment(p, line[k], line[k + 1]));
  return best;
}

function cumulative(line: LngLat[]): number[] {
  const out = [0];
  for (let i = 1; i < line.length; i++) out.push(out[i - 1] + metres(line[i - 1], line[i]));
  return out;
}

/**
 * The line resampled to one point every 50 m (at most 500, at least 2), evenly by distance:
 * the first and last points always, and between them the vertex at or just past each step.
 */
export function samplesForMatching(line: LngLat[]): LngLat[] {
  const coords = line.map(c => [c[0], c[1]]);
  if (coords.length <= 2) return coords;
  const along = cumulative(coords);
  const total = along[along.length - 1];
  const count = Math.min(MAX_MATCH_POINTS, Math.max(2, Math.ceil(total / MATCH_SAMPLE_SPACING_M) + 1));
  if (coords.length <= count) return coords;

  const step = total / (count - 1);
  const kept = [coords[0]];
  let next = step;
  for (let i = 1; i < coords.length - 1 && kept.length < count - 1; i++) {
    if (along[i] >= next) {
      kept.push(coords[i]);
      while (next <= along[i]) next += step;
    }
  }
  kept.push(coords[coords.length - 1]);
  return kept;
}

/** Where the matched line leaves itself and comes back: indices into it, and its far end. */
export interface Detour {
  from: number;
  to: number;
  tip: LngLat;
  reach: number;
  length: number;
}

/**
 * The next index from `k` that could be within `radius` of a point `d` metres from line[k]:
 * the line can't close a gap faster than it travels, so it has to go at least `d - radius`
 * further along first. Skipping to there leaves every result as a point-by-point scan finds it,
 * but turns a scan over kilometres of line into a handful of distance checks.
 */
function skipAhead(along: number[], k: number, d: number, radius: number): number {
  const target = along[k] + (d - radius) - 1e-6;
  let next = k + 1;
  while (next < along.length && along[next] < target) next++;
  return next;
}

/**
 * Whether the original line itself goes out to this detour and back: it passes the detour's
 * start twice, with a short stretch between that gets about as far out as the detour does.
 * `originalAlong` is the original's running length, when the caller already has it.
 */
export function drawnByAuthor(
  original: LngLat[],
  start: LngLat,
  reach: number,
  length: number,
  originalAlong: number[] = cumulative(original)
): boolean {
  const radius = nearFor(reach);
  let lastNear = -1;
  let k = 0;
  while (k < original.length) {
    const d = metres(original[k], start);
    if (d > radius) {
      k = skipAhead(originalAlong, k, d, radius);
      continue;
    }
    // Two passes of the start with a stretch between: short enough, and out far enough?
    if (lastNear >= 0 && k > lastNear + 1 && originalAlong[k] - originalAlong[lastNear] <= 2 * length + 100) {
      for (let m = lastNear + 1; m <= k; m++) if (metres(original[m], start) >= 0.5 * reach) return true;
    }
    lastNear = k;
    k += 1;
  }
  return false;
}

/** Every detour in `matched` that the `original` line doesn't make itself, outermost first. */
export function findDetours(matched: LngLat[], original: LngLat[]): Detour[] {
  const along = cumulative(matched);
  const originalAlong = cumulative(original);
  const out: Detour[] = [];
  let i = 0;
  while (i < matched.length - 2) {
    // The last j within MAX_DETOUR_M that comes back to within RETURN_M of i.
    let back = -1;
    let j = i + 2;
    while (j < matched.length && along[j] - along[i] <= MAX_DETOUR_M) {
      const d = metres(matched[i], matched[j]);
      if (d <= RETURN_M) {
        back = j;
        j += 1;
      } else {
        j = skipAhead(along, j, d, RETURN_M);
      }
    }
    // Its farthest point from i, strictly between: far enough out to be a detour, not a wiggle?
    let found: Detour | null = null;
    if (back >= 0) {
      let reach = 0;
      let tip = matched[i];
      for (let k = i + 1; k < back; k++) {
        const d = metres(matched[i], matched[k]);
        if (d > reach) {
          reach = d;
          tip = matched[k];
        }
      }
      if (reach >= MIN_REACH_M) found = { from: i, to: back, tip, reach, length: along[back] - along[i] };
    }
    if (found && !drawnByAuthor(original, matched[found.from], found.reach, found.length, originalAlong)) {
      out.push(found);
      i = found.to;
    } else {
      i += 1;
    }
  }
  return out;
}

/**
 * The samples without the ones that pulled the match into `detours` (found in `matched`): for
 * each, the sample nearest its tip and any within 10 m further, and every sample that lies
 * nearer the detour than the rest of the matched line — the ones the matcher went out there to
 * reach. A sample by where the detour leaves is as near the line as the detour, so it stays.
 * The first and last samples always stay.
 */
export function withoutDetourSamples(samples: LngLat[], detours: Detour[], matched: LngLat[]): LngLat[] {
  const drop = new Set<number>();
  const last = matched.length - 1;
  for (const detour of detours) {
    const distances = samples.map(s => metres(s, detour.tip));
    let nearest = Infinity;
    for (let k = 1; k < samples.length - 1; k++) nearest = Math.min(nearest, distances[k]);
    for (let k = 1; k < samples.length - 1; k++) if (distances[k] <= nearest + DROP_SLACK_M) drop.add(k);

    if (detour.from < 0 || detour.to > last || detour.to <= detour.from) continue;
    for (let k = 1; k < samples.length - 1; k++) {
      if (drop.has(k)) continue;
      const inside = metresToStretch(samples[k], matched, detour.from, detour.to);
      const before = metresToStretch(samples[k], matched, 0, detour.from);
      const after = metresToStretch(samples[k], matched, detour.to, last);
      if (inside < Math.min(before, after)) drop.add(k);
    }
  }
  return samples.filter((_, k) => !drop.has(k));
}

/**
 * Matches `line`, then asks again without the points behind any detour, up to four requests.
 * The first request failing fails the match; a retry failing keeps the answer before it; the
 * last answer is kept even if a detour is still in it.
 */
export async function matchWithoutDetours(
  line: LngLat[],
  mapMatch: (coords: number[][], profile: string) => Promise<MapMatchResult>,
  profile: string
): Promise<MapMatchResult> {
  let samples = samplesForMatching(line);
  let result = await mapMatch(samples, profile);
  for (let attempt = 1; attempt < MAX_MATCH_ATTEMPTS; attempt++) {
    const matched = result.graphooper.paths?.[0]?.points?.coordinates ?? [];
    const detours = findDetours(matched, line);
    if (!detours.length) return result;
    const fewer = withoutDetourSamples(samples, detours, matched);
    if (fewer.length === samples.length) return result;
    samples = fewer;
    try {
      result = await mapMatch(samples, profile);
    } catch {
      return result;
    }
  }
  return result;
}
